import React, {ReactNode, useEffect, useState} from "react";
import {
	Avatar,
	Box,
	Button,
	Card,
	Chip,
	FormControlLabel,
	Grid,
	InputAdornment,
	Pagination,
	Skeleton,
	Stack,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
	useMediaQuery,
} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";
import {
	EvaluationList,
	EvaluationRestControllerApiFactory,
	PageDtoEvaluationList,
	PersonRestControllerApiFactory,
} from "arteco-api-client-ts";
import {useNavigate, useParams} from "react-router-dom";
import dayjs from "dayjs";
import AdminInner from "../../../../components/layout/admin_inner";
import {useApiManager} from "../../../../components/utils/api";
import {parsePathId} from "../../../../components/form/form_utils";
import {dateToString} from "../../../../components/utils/dates";
import {avatarColor, btnSx, fullName, initials, panelSx, relative} from "../interview/ui";
import {firstComment, formatScore, ScoreBadge, ScoreBar, scoreColor} from "./ui";

const PAGE_SIZE = 25;

/** Desde la primera autoevaluación registrada (2022) hasta el año actual. */
const YEARS = (() => {
	const years: number[] = [];
	for (let y = dayjs().year(); y >= 2022; y--) {
		years.push(y);
	}
	return years;
})();

const average = (list: EvaluationList[]) => {
	const rated = list.filter(e => e.rating !== undefined && e.rating !== null);
	return rated.length ? rated.reduce((a, e) => a + (e.rating as number), 0) / rated.length : undefined;
};

const Figure = ({label, children, hint}: { label: string, children: ReactNode, hint?: ReactNode }) => (
	<Grid item xs={4}>
		<Box sx={{p: {xs: 1.5, sm: 2}, borderRadius: 2.5, border: "1px solid", borderColor: "divider", height: "100%", bgcolor: "background.paper"}}>
			<Typography variant="body2" color="text.secondary" sx={{fontSize: {xs: 12, sm: 14}}}>{label}</Typography>
			<Box sx={{mt: 0.5}}>{children}</Box>
			{hint}
		</Box>
	</Grid>
);

/** Cifras del mes en curso frente al anterior: cuántas y su media. */
const MonthFigures = ({personId}: { personId?: number }) => {
	const apiManager = useApiManager();
	const ctrl = apiManager.factory(EvaluationRestControllerApiFactory);
	const [current, setCurrent] = useState<EvaluationList[]>();
	const [previous, setPrevious] = useState<EvaluationList[]>();

	useEffect(() => {
		const now = dayjs();
		const prev = now.subtract(1, "month");
		apiManager.execute(ctrl.listEvaluation(personId, now.year(), now.month() + 1)).then(setCurrent).catch(() => setCurrent([]));
		apiManager.execute(ctrl.listEvaluation(personId, prev.year(), prev.month() + 1)).then(setPrevious).catch(() => setPrevious([]));
	}, [personId]);

	const cur = current && average(current);
	const prev = previous && average(previous);
	const delta = cur !== undefined && prev !== undefined ? (cur - prev) / 4 : undefined;
	const month = dayjs().format("MMMM");

	return (
		<Grid container spacing={1.5} sx={{mb: 3}}>
			<Figure label={`Autoevaluaciones de ${month}`}>
				{current ? <Typography variant="h5" fontWeight={700}>{current.length}</Typography> : <Skeleton width={40} height={36}/>}
			</Figure>
			<Figure label={`Media de ${month}`} hint={delta !== undefined && Math.abs(delta) >= 0.05 && (
				<Stack direction="row" spacing={0.5} alignItems="center" sx={{mt: 0.5, color: delta > 0 ? "success.main" : "error.main"}}>
					{delta > 0 ? <TrendingUpRoundedIcon fontSize="small"/> : <TrendingDownRoundedIcon fontSize="small"/>}
					<Typography variant="caption" fontWeight={600}>
						{delta > 0 ? "+" : ""}{delta.toLocaleString("es-ES", {maximumFractionDigits: 1})} frente al mes anterior
					</Typography>
				</Stack>
			)}>
				{current ? (cur !== undefined ? <ScoreBadge rating={cur}/> : <Typography variant="h5" color="text.disabled">—</Typography>)
					: <Skeleton width={60} height={36}/>}
			</Figure>
			<Figure label={`Media de ${dayjs().subtract(1, "month").format("MMMM")}`}>
				{previous ? (prev !== undefined ? <ScoreBadge rating={prev}/> : <Typography variant="h5" color="text.disabled">—</Typography>)
					: <Skeleton width={60} height={36}/>}
			</Figure>
		</Grid>
	);
};

/**
 * Autoevaluaciones de la plantilla (#13823): cifras del mes, búsqueda por nombre o NIF, filtro por año y
 * eliminadas aparte; la puntuación sobre 10. Con persona en la ruta, solo las suyas.
 */
export const EvaluationListPage = () => {
	const apiManager = useApiManager();
	const navigate = useNavigate();
	const params = useParams();
	const theme = useTheme();
	const compact = useMediaQuery(theme.breakpoints.down("md"));
	const personId = parsePathId(params, "personId");
	const ctrl = apiManager.factory(EvaluationRestControllerApiFactory);

	const [page, setPage] = useState(1);
	const [term, setTerm] = useState("");
	const [query, setQuery] = useState("");
	const [year, setYear] = useState<number>();
	const [showDismissed, setShowDismissed] = useState(false);
	const [result, setResult] = useState<PageDtoEvaluationList>();
	const [personName, setPersonName] = useState<string>();

	useEffect(() => {
		const t = setTimeout(() => {
			setQuery(term.trim());
			setPage(1);
		}, 300);
		return () => clearTimeout(t);
	}, [term]);

	useEffect(() => {
		setResult(undefined);
		apiManager.execute(ctrl.pageEvaluation(page - 1, PAGE_SIZE, undefined, personId, year, undefined, undefined,
			query || undefined, showDismissed || undefined))
			.then(setResult)
			.catch(() => setResult({content: [], totalElements: 0, totalPages: 0}));
	}, [page, query, year, showDismissed, personId]);

	useEffect(() => {
		if (personId) {
			apiManager.execute(apiManager.factory(PersonRestControllerApiFactory).summaryPerson(personId))
				.then(s => setPersonName(fullName(s.person))).catch(() => undefined);
		}
	}, [personId]);

	const base = personId ? "/app/admin/employee/person/" + personId + "/evaluation" : "/app/admin/employee/evaluation";
	const open = (e: EvaluationList) => navigate(base + "/" + e.id);
	const rows = result?.content || [];
	const total = result?.totalElements ?? 0;

	const PersonCell = ({e}: { e: EvaluationList }) => {
		const name = fullName(e.person);
		return (
			<Stack direction="row" spacing={1.5} alignItems="center" sx={{minWidth: 0}}>
				<Avatar sx={{width: 32, height: 32, fontSize: 12, fontWeight: 700, bgcolor: avatarColor(name)}}>{initials(name)}</Avatar>
				<Typography variant="body2" fontWeight={600} noWrap>{name}</Typography>
			</Stack>
		);
	};

	return (
		<AdminInner withoutPaper>
			<Box sx={{maxWidth: 1180}}>
				<Stack direction={{xs: "column", sm: "row"}} spacing={2} justifyContent="space-between" alignItems={{sm: "flex-end"}} sx={{mb: 3}}>
					<Box sx={{minWidth: 0}}>
						{personId && (
							<Button size="small" startIcon={<ArrowBackRoundedIcon/>} onClick={() => navigate(-1)}
									sx={{...btnSx, ml: -1, mb: 0.5, color: "text.secondary"}}>
								Volver
							</Button>
						)}
						<Typography variant="h5" component="h1" fontWeight={700}>
							{personId ? `Autoevaluaciones de ${personName || "…"}` : "Autoevaluaciones"}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{result ? `${total} ${total === 1 ? "autoevaluación" : "autoevaluaciones"}${year ? " en " + year : ""}` : " "}
						</Typography>
					</Box>
					<Button variant="outlined" startIcon={<AddRoundedIcon/>} onClick={() => navigate(base + "/new")}
							sx={{...btnSx, alignSelf: {xs: "flex-start", sm: "auto"}}}>
						Registrar una
					</Button>
				</Stack>

				<MonthFigures personId={personId}/>

				<Card elevation={0} sx={panelSx}>
					<Stack direction={{xs: "column", md: "row"}} spacing={1.5} justifyContent="space-between" alignItems={{md: "center"}} sx={{mb: 2}}>
						<Stack direction="row" spacing={1} sx={{overflowX: "auto", pb: {xs: 0.5, md: 0}}}>
							<Chip label="Todos los años" clickable onClick={() => {
								setYear(undefined);
								setPage(1);
							}} color={!year ? "primary" : "default"} variant={!year ? "filled" : "outlined"} sx={{fontWeight: 600, flexShrink: 0}}/>
							{YEARS.map(y => (
								<Chip key={y} label={y} clickable onClick={() => {
									setYear(y);
									setPage(1);
								}} color={year === y ? "primary" : "default"} variant={year === y ? "filled" : "outlined"}
									  sx={{fontWeight: 600, flexShrink: 0}}/>
							))}
						</Stack>
						<Stack direction="row" spacing={2} alignItems="center">
							<FormControlLabel sx={{mr: 0, whiteSpace: "nowrap"}}
											  control={<Switch size="small" checked={showDismissed} onChange={e => {
												  setShowDismissed(e.target.checked);
												  setPage(1);
											  }}/>}
											  label={<Typography variant="body2">Eliminadas</Typography>}/>
							{!personId && (
								<TextField size="small" placeholder="Buscar persona o NIF" value={term} onChange={e => setTerm(e.target.value)}
										   sx={{width: {xs: "100%", md: 260}, "& .MuiOutlinedInput-root": {borderRadius: 2}}}
										   InputProps={{startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small"/></InputAdornment>}}/>
							)}
						</Stack>
					</Stack>

					{!result ? (
						<Stack spacing={1}>{[0, 1, 2, 3, 4].map(i => <Skeleton key={i} variant="rounded" height={52}/>)}</Stack>
					) : rows.length === 0 ? (
						<Box sx={{py: 6, textAlign: "center"}}>
							<Typography variant="body2" color="text.secondary">
								{query || year ? "Ninguna autoevaluación con ese filtro." : "Aún no hay autoevaluaciones."}
							</Typography>
						</Box>
					) : compact ? (
						<Stack spacing={1.5}>
							{rows.map(e => (
								<Box key={e.id} role="link" tabIndex={0} onClick={() => open(e)} onKeyDown={k => k.key === "Enter" && open(e)}
									 sx={{
										 p: 2, borderRadius: 2.5, border: "1px solid", borderColor: "divider", cursor: "pointer",
										 opacity: e.dismissed ? 0.55 : 1, "&:hover": {borderColor: "primary.main"},
									 }}>
									<Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
										{personId
											? <Typography variant="body2" fontWeight={600}>{dateToString(e.evaluationDate)}</Typography>
											: <PersonCell e={e}/>}
										<ScoreBadge rating={e.rating}/>
									</Stack>
									{!personId && <Typography variant="caption" color="text.secondary">{dateToString(e.evaluationDate)} · {relative(e.evaluationDate)}</Typography>}
									{firstComment(e) && (
										<Typography variant="body2" color="text.secondary" sx={{
											mt: 1, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
										}}>{firstComment(e)}</Typography>
									)}
								</Box>
							))}
						</Stack>
					) : (
						<TableContainer sx={{border: "1px solid", borderColor: "divider", borderRadius: 2.5}}>
							<Table size="small" sx={{
								tableLayout: "fixed",
								"& th": {bgcolor: "grey.50", color: "text.secondary", fontSize: 12, fontWeight: 700, py: 1.25, whiteSpace: "nowrap"},
								"& td": {py: 1.25, borderColor: "divider"},
								"& tbody tr:last-of-type td": {borderBottom: 0},
							}}>
								<TableHead>
									<TableRow>
										<TableCell sx={{width: 130}}>Fecha</TableCell>
										{!personId && <TableCell sx={{width: 260}}>Persona</TableCell>}
										<TableCell sx={{width: 200}}>Puntuación</TableCell>
										<TableCell>Comentarios</TableCell>
										<TableCell sx={{width: 44}}/>
									</TableRow>
								</TableHead>
								<TableBody>
									{rows.map(e => (
										<TableRow key={e.id} hover onClick={() => open(e)} sx={{cursor: "pointer", opacity: e.dismissed ? 0.5 : 1}}>
											<TableCell>
												<Typography variant="body2" fontWeight={600} sx={{fontVariantNumeric: "tabular-nums"}}>{dateToString(e.evaluationDate)}</Typography>
												<Typography variant="caption" color="text.secondary">{relative(e.evaluationDate)}</Typography>
											</TableCell>
											{!personId && <TableCell><PersonCell e={e}/></TableCell>}
											<TableCell>
												<Stack direction="row" spacing={1.5} alignItems="center">
													<Typography variant="body2" fontWeight={700} sx={{color: scoreColor(e.rating), width: 32, fontVariantNumeric: "tabular-nums"}}>
														{formatScore(e.rating)}
													</Typography>
													<Box sx={{flexGrow: 1}}><ScoreBar rating={e.rating}/></Box>
												</Stack>
											</TableCell>
											<TableCell>
												<Typography variant="body2" color="text.secondary" noWrap title={firstComment(e)}>
													{e.dismissed && <Chip size="small" label="Eliminada" sx={{mr: 1, height: 20}}/>}
													{firstComment(e) || "—"}
												</Typography>
											</TableCell>
											<TableCell align="right" sx={{color: "text.disabled"}}><ChevronRightRoundedIcon fontSize="small"/></TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					)}

					{(result?.totalPages || 0) > 1 && (
						<Stack alignItems="center" sx={{mt: 3}}>
							<Pagination count={result?.totalPages} page={page} onChange={(_, p) => setPage(p)} shape="rounded"
										size={compact ? "small" : "medium"}/>
						</Stack>
					)}
				</Card>
			</Box>
		</AdminInner>
	);
};
